import React from 'react'
import {useSelector} from 'react-redux'
import {
  DealFreezeOfferBoxProvider,
  DealFreezeOfferBoxSlot,
  useDealFreezeOfferBoxContext
} from 'components/Accommodation/Offers/DealFreezeOfferBox'
import {PriceWatchCard} from 'components/Accommodation/PriceWatchCard/PriceWatchCard'
import {PriceWatchSkeleton} from 'components/Accommodation/PriceWatchCard/PriceWatchSkeleton'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getSplitBookingDetails} from 'modules/sapiSearch/selectors'
import {getTopOffersWithSplitBookings} from 'modules/sapiSearch/utils'
import {getIsNoDatesSearch} from 'modules/search/selectors'

import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {HotelOfferCard, HotelOfferCardSkeleton} from '../../HotelOfferCard'
import {useAccommodationTopOffers} from '../../useAccommodationTopOffers'
import {useShowAllOffersFromUrl} from '../../useShowAllOffersFromUrl'
import {AllOffersDesktopOverlay} from './AllOffersDesktopOverlay'
import {MoreDealsDesktopButton} from './MoreDealsDesktopButton'
import {
  LayoutVariant,
  TOP_OFFERS_COUNT,
  TopOffersDesktopLayout
} from './TopOffersDesktopLayout'
import {TopOffersDesktopSoldOut} from './TopOffersDesktopSoldOut'
import {TopOffersPriceWatchToggle} from './TopOffersPriceWatchToggle'

const SOURCE_COMPONENT = 'DesktopHotelViewTopOffers'
// The inline nav unmounts mid-scroll and shifts the page, which would leave
// the filter row under the sticky header
const DEAL_FREEZE_SCROLL_TARGET = AccommodationPageNavSections.Rooms

const TopOffersDesktopContent = () => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {offerEntity, isComplete, hasAnyOffers} = useAccommodationTopOffers({
    hotelId
  })
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  // Reuse the shared split-booking merge so the injected bundle and resulting
  // offer ordering match what the A-side OfferBox shows.
  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId ?? '')
  )
  const offers = offerEntity
    ? getTopOffersWithSplitBookings(offerEntity, splitBookingDetails)
    : []

  const {hasSplitBookingOffer} = splitBookingDetails

  const {retention} = useBrandCapabilities()
  const {hasPriceWatch} = retention

  const isLoading = !hasAnyOffers && !isNoDatesSearch
  const isSoldout = isComplete && !(hasSplitBookingOffer || hasAnyOffers)

  const dealFreezeOfferBox = useDealFreezeOfferBoxContext()
  // A freezable Vio top offer carries the tag instead, so the card only shows when it is not freezable
  const dealFreezeCardState =
    dealFreezeOfferBox?.isBannerVisible && !dealFreezeOfferBox.isTagVisible
      ? dealFreezeOfferBox
      : null

  const isPriceWatchPending =
    hasPriceWatch && Boolean(dealFreezeOfferBox?.isDecisionPending)

  // A lone offer with no fourth cell renders full-width instead of a single
  // narrow card.
  const variant: LayoutVariant =
    offers.length === 1 && !hasPriceWatch && !dealFreezeCardState
      ? 'horizontal'
      : 'vertical'

  const topOffers = offers.slice(0, TOP_OFFERS_COUNT)

  const hasMoreOffers = (offerEntity?.offers?.length ?? 0) > TOP_OFFERS_COUNT
  useShowAllOffersFromUrl({
    hotelId,
    hasMoreOffers: isComplete && hasMoreOffers
  })

  const title = (
    <Text variant="titleL">
      <FormattedMessageWrapper
        defaultMessage="Available deals"
        id="availableDealsTopOffers"
      />
    </Text>
  )

  const offerCards = isLoading
    ? Array.from({length: TOP_OFFERS_COUNT}).map((_, index) => (
        <HotelOfferCardSkeleton key={index} />
      ))
    : topOffers.map((offer, index) => (
        <HotelOfferCard
          key={offer.id}
          offer={offer}
          offerPosition={index}
          sourceComponent={SOURCE_COMPONENT}
          variant={variant}
        />
      ))

  return (
    <>
      <AllOffersDesktopOverlay />
      <Box display="flex" flexDirection="column" gap="s500">
        {dealFreezeCardState && hasPriceWatch ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="s400"
          >
            {title}
            <TopOffersPriceWatchToggle />
          </Box>
        ) : (
          title
        )}
        {isSoldout ? (
          <TopOffersDesktopSoldOut />
        ) : (
          <TopOffersDesktopLayout
            offerCards={offerCards}
            priceWatchSection={
              dealFreezeCardState ? (
                <DealFreezeOfferBoxSlot
                  hotelId={hotelId}
                  sourceComponent={SOURCE_COMPONENT}
                  state={dealFreezeCardState}
                  scrollTarget={DEAL_FREEZE_SCROLL_TARGET}
                  variant="card"
                />
              ) : isPriceWatchPending ? (
                <PriceWatchSkeleton />
              ) : hasPriceWatch ? (
                <PriceWatchCard offersLength={offers.length} />
              ) : null
            }
            xsSection={
              dealFreezeCardState ? (
                <DealFreezeOfferBoxSlot
                  hotelId={hotelId}
                  sourceComponent={SOURCE_COMPONENT}
                  state={dealFreezeCardState}
                  scrollTarget={DEAL_FREEZE_SCROLL_TARGET}
                />
              ) : null
            }
            moreDealsButtonSlot={
              hasMoreOffers ? (
                <MoreDealsDesktopButton hotelId={hotelId} />
              ) : null
            }
          />
        )}
      </Box>
    </>
  )
}

export const TopOffersDesktopRedesigned = () => {
  const hotelId = useSelector(getAccommodationPageHotelId)

  return (
    <DealFreezeOfferBoxProvider hotelId={hotelId ?? ''} surface="desktop">
      <TopOffersDesktopContent />
    </DealFreezeOfferBoxProvider>
  )
}
